import {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useState,
} from 'react'
import useProductMain, {
    useProductMainType,
} from './hooks/useProductMain/useProductMain'
import { ProductContract } from 'models'
import { isEmpty, isUndefined } from 'lodash-es'
import nameof from 'utilities/nameof'
import RequestToRemoveIngredientDialog from 'pages/IngredientsPage/Ingredients/components/RequestToRemoveIngredientDialog'
import { AvailabilityOptions } from './hooks/useProductMain/useBasicProductLookupFilter'
import { StatusOptions } from 'components/Selector/StatusSelector'

const PRODUCT_FIELD = nameof<ProductContract>()

interface IProductPageContext extends useProductMainType {
    categoryFiltered: string[]
    setCategoryFiltered: Dispatch<SetStateAction<string[]>>
    availabilityFiltered: AvailabilityOptions
    setAvailabilityFiltered: Dispatch<SetStateAction<AvailabilityOptions>>
    statusFiltered: StatusOptions
    setStatusFiltered: Dispatch<SetStateAction<StatusOptions>>
}
/**
 * ProductPageContext
 * for case save filter value even click into child page
 */
export const ProductPageContext = createContext<IProductPageContext>(
    {} as IProductPageContext,
)

const ProductContextProvider = ({ children }: { children: any }) => {
    const productHook = useProductMain()

    const { handleOnToggleSwitch } = productHook

    const [requestToDeactivateItem, setRequestToDeactivateItem] = useState<
        ProductContract | undefined
    >()

    const [isForceRequest, setIsForceRequest] = useState<boolean>(false)

    //State for contain filter value in Admin product page
    const [categoryFiltered, setCategoryFiltered] = useState<string[]>([])
    const [availabilityFiltered, setAvailabilityFiltered] = useState(
        AvailabilityOptions.All,
    )
    const [statusFiltered, setStatusFiltered] = useState<StatusOptions>(
        StatusOptions.All,
    )

    const onUpdateFlag = useCallback(
        async (
            key: string,
            toggleItem: ProductContract,
            isForce: boolean = false,
        ) => {
            const newValue: boolean = !(toggleItem as any)[key]
            if (
                key === PRODUCT_FIELD.isActive &&
                !newValue &&
                (!isEmpty(toggleItem.elementOfItemUnits ?? []) ||
                    !isEmpty(toggleItem.modifierOptions ?? []) ||
                    toggleItem.itemVariations?.some(
                        (iv) =>
                            !isEmpty(iv.elementOfItemUnits ?? []) ||
                            !isEmpty(iv.modifierOptions ?? []),
                    ))
            ) {
                // Request to Remove this item from related items/options
                setIsForceRequest(isForce)
                setRequestToDeactivateItem(toggleItem)
            } else {
                await handleOnToggleSwitch(key, toggleItem, isForce)
            }
        },
        [handleOnToggleSwitch],
    )

    const onRemoveAndDeactivateIngredient = useCallback(() => {
        if (requestToDeactivateItem)
            handleOnToggleSwitch(
                PRODUCT_FIELD.isActive,
                requestToDeactivateItem,
                isForceRequest,
            )
        setRequestToDeactivateItem(undefined)
        setIsForceRequest(false)
    }, [requestToDeactivateItem, isForceRequest, handleOnToggleSwitch])
    return (
        <ProductPageContext.Provider
            value={{
                ...productHook,
                handleOnToggleSwitch: onUpdateFlag,
                categoryFiltered,
                setCategoryFiltered,
                availabilityFiltered,
                setAvailabilityFiltered,
                statusFiltered,
                setStatusFiltered,
            }}
        >
            {children}
            <RequestToRemoveIngredientDialog
                open={!isUndefined(requestToDeactivateItem)}
                onClose={() => setRequestToDeactivateItem(undefined)}
                onRemoveAndDeactivateIngredient={
                    onRemoveAndDeactivateIngredient
                }
                elementOfItemUnits={
                    !isEmpty(requestToDeactivateItem?.elementOfItemUnits ?? [])
                        ? requestToDeactivateItem?.elementOfItemUnits ?? []
                        : requestToDeactivateItem?.itemVariations?.flatMap(
                              (i) => i.elementOfItemUnits ?? [],
                          ) ?? []
                }
                modifierOptions={
                    !isEmpty(requestToDeactivateItem?.modifierOptions ?? [])
                        ? requestToDeactivateItem?.modifierOptions ?? []
                        : requestToDeactivateItem?.itemVariations?.flatMap(
                              (i) => i.modifierOptions ?? [],
                          ) ?? []
                }
                itemType="item"
            />
        </ProductPageContext.Provider>
    )
}

export const useProductContext = () => {
    return useContext(ProductPageContext)
}

export default ProductContextProvider
